/**
 * UserManagementEditPage — mirrors Angular UserManagementEditComponent.
 * Business logic from USER_MANAGEMENT_MODULE.md §4.
 */
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Loader2, User, Shield, FileText, Building2, Plus } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { cn } from '@/lib/utils/helpers'
import { AssignRoleModal } from '../components/AssignRoleModal'
import { SectionCard } from '../components/edit/SectionCard'
import { UserNotesSection } from '../components/edit/UserNotesSection'
import { UserManagementEditHeader } from '../components/edit/UserManagementEditHeader'
import { UserManagementPersonalInfoForm } from '../components/edit/UserManagementPersonalInfoForm'
import { UserManagementRolesSection } from '../components/edit/UserManagementRolesSection'
import { UserManagementAssignPropertiesSection } from '../components/edit/UserManagementAssignPropertiesSection'
import { UserManagementEditDialogs } from '../components/edit/UserManagementEditDialogs'
import { useUserManagementEditPage } from '../hooks/useUserManagementEditPage'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import type { UserManagementEditPageProps } from '../types'

export function UserManagementEditPage({
  embeddedUserId,
  embedded = false,
  onEmbeddedClose,
}: UserManagementEditPageProps = {}) {
  const { userId: paramUserId } = useParams<{ userId: string }>()
  const userId = embeddedUserId ?? paramUserId
  const navigate = useNavigate()
  const page = useUserManagementEditPage(userId)

  if (page.loading) {
    return (
      <div
        className={cn('bg-[#f8fafc] font-sans', !embedded && 'min-h-screen')}
        data-testid={embedded ? 'um-edit-embedded-loading' : undefined}
      >
        {!embedded && <PageHeader title="USER MANAGEMENT" />}
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
        </div>
      </div>
    )
  }

  if (!page.user) {
    return (
      <div
        className={cn('bg-[#f8fafc] font-sans', !embedded && 'min-h-screen')}
        data-testid={embedded ? 'um-edit-embedded-not-found' : undefined}
      >
        {!embedded && <PageHeader title="USER MANAGEMENT" />}
        <div className="w-full px-3 sm:px-6 py-12 text-center text-slate-500">User not found.</div>
      </div>
    )
  }

  const { user } = page

  return (
    <div
      className={cn(
        'bg-[#f8fafc] font-sans text-slate-900',
        embedded ? 'min-h-0 pb-8' : 'min-h-screen pb-12',
      )}
      data-testid={embedded ? 'um-edit-embedded-root' : undefined}
    >
      {!embedded && <PageHeader title="USER MANAGEMENT" />}

      <div className="w-full px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-5">
        <SectionCard>
          <UserManagementEditHeader
            userId={userId}
            lastActive={user.last_active}
            updatedAt={user.updated_at}
            loginCount={user.login_count}
            avgLogin={user.avg_login}
          />
        </SectionCard>

        {page.hideForm && (
          <SectionCard>
            <p className="text-sm text-slate-500 text-center py-2">
              This is a Liberty user. You do not have permission to edit their details.
            </p>
          </SectionCard>
        )}

        {!page.hideForm && (
          <SectionCard icon={User} title="Personal Information">
            <UserManagementPersonalInfoForm
              firstName={page.firstName}
              setFirstName={page.setFirstName}
              lastName={page.lastName}
              setLastName={page.setLastName}
              setLastNameTouched={page.setLastNameTouched}
              phone={page.phone}
              setPhone={page.setPhone}
              email={page.email}
              setEmail={page.setEmail}
              jobTitle={page.jobTitle}
              setJobTitle={page.setJobTitle}
              refundActionNotif={page.refundActionNotif}
              setRefundActionNotif={page.setRefundActionNotif}
              refundActionEmail={page.refundActionEmail}
              setRefundActionEmail={page.setRefundActionEmail}
              invoiceActionNotif={page.invoiceActionNotif}
              setInvoiceActionNotif={page.setInvoiceActionNotif}
              invoiceActionEmail={page.invoiceActionEmail}
              setInvoiceActionEmail={page.setInvoiceActionEmail}
              formErrors={page.formErrors}
              setFormErrors={page.setFormErrors}
              submitting={page.submitting}
              getFirstNameError={page.getFirstNameError}
              getLastNameError={page.getLastNameError}
              getPhoneError={page.getPhoneError}
              onSubmit={page.handleSubmit}
              onCancel={() =>
                embedded && onEmbeddedClose ? onEmbeddedClose() : navigate('/user-management')
              }
              emailVerifyStatus={user.email_verify}
              accountStatus={page.accountStatus}
              isActive={page.isActive}
              hasEmail={page.hasEmail}
              canTempPassword={page.canTempPassword}
              canDisableEnable={page.canDisableEnable}
              showNotificationSettings={page.showNotificationSettings}
              actionDetails={page.actionDetails}
              tempPassword={page.tempPassword}
              setTempPassword={page.setTempPassword}
              showTempPwd={page.showTempPwd}
              setShowTempPwd={page.setShowTempPwd}
              settingTempPwd={page.settingTempPwd}
              togglingStatus={page.togglingStatus}
              verifyingEmail={page.verifyingEmail}
              resettingPwd={page.resettingPwd}
              onRequestTempPassword={() => page.setConfirmOpen('temp-password')}
              onRequestAccountToggle={() => page.setConfirmOpen('account-toggle')}
              onVerifyEmail={page.handleVerifyEmail}
              onRequestResetPassword={() => page.setConfirmOpen('reset-password')}
            />
          </SectionCard>
        )}

        <SectionCard
          icon={Shield}
          title="Roles"
          headerActions={
            page.hasEmail ? (
              <Link
                to={`/user-management/roles/${userId}`}
                className={cn(primaryEntityActionButtonClass, 'h-10 px-4 text-sm font-semibold rounded-xl gap-2')}
                data-testid="um-edit-assign-new-role-link"
              >
                <Plus className="h-4 w-4 shrink-0" aria-hidden />
                Assign New Role
              </Link>
            ) : (
              <span
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-400 cursor-not-allowed"
                data-testid="um-edit-assign-new-role-disabled"
              >
                <Plus className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
                Assign New Role
              </span>
            )
          }
        >
          <UserManagementRolesSection
            user={user}
            canEditRole={page.canEditRole}
            canShowFinMgr={page.canShowFinMgr}
            canRemoveRoleItem={page.canRemoveRoleItem}
            canImpersonateRole={page.canImpersonateRole}
            onRequestFinanceManager={page.requestFinanceManager}
            onRequestRemoveRole={page.requestRemoveRole}
            onRequestImpersonate={(roleId, roleName) => {
              void page.handleImpersonate(roleId, roleName)
            }}
            onRequestSalesRole={page.requestSalesRole}
            onEditRole={(role) => {
              page.setEditingRole(role)
              page.setAssignRoleOpen(true)
            }}
          />
        </SectionCard>

        {page.canShowRegionalManagerSelfAssign && (
          <SectionCard icon={Building2} title="Assign Properties">
            <UserManagementAssignPropertiesSection
              showAssignProps={page.showAssignProps}
              assignPropOptions={page.assignPropOptions}
              assignPropLoading={page.assignPropLoading}
              assignPropValue={page.assignPropValue}
              assignPropSubmitting={page.assignPropSubmitting}
              onToggle={page.toggleAssignProperties}
              onAssignPropChange={page.setAssignPropValue}
              onOpen={() => void page.loadAssignProperties()}
              onSearch={page.handleAssignPropSearch}
              onSubmit={page.handleAssignPropSubmit}
              onCancel={() => {
                page.setShowAssignProps(false)
                page.resetAssignProperties()
              }}
            />
          </SectionCard>
        )}

        {page.canAddNotes && userId && (
          <SectionCard icon={FileText} title="Notes">
            <UserNotesSection
              userId={userId}
              isLibertyRole={page.isLibertyRole}
              currentUserId={page.currentUserId}
              refreshSignal={page.notesRefreshKey}
            />
          </SectionCard>
        )}
      </div>

      <AssignRoleModal
        isOpen={page.assignRoleOpen}
        onClose={page.closeAssignRoleModal}
        userId={userId ?? ''}
        existingRole={page.editingRole}
        existingRoleKeys={user.roles?.map((role) => role.role_name) ?? []}
        onSuccess={() => void page.fetchUser()}
      />

      <UserManagementEditDialogs
        confirmOpen={page.confirmOpen}
        setConfirmOpen={page.setConfirmOpen}
        isActive={page.isActive}
        userEmail={user.email}
        user={user}
        finMgrRoleId={page.finMgrRoleId}
        pendingSalesRole={page.pendingSalesRole}
        setRemovingRoleId={page.setRemovingRoleId}
        setFinMgrRoleId={page.setFinMgrRoleId}
        impersonateTarget={page.impersonateTarget}
        setImpersonateTarget={page.setImpersonateTarget}
        resetSalesRoleFlow={page.resetSalesRoleFlow}
        onSetTempPassword={page.handleSetTempPassword}
        onAccountToggle={page.handleAccountToggle}
        onResetPassword={page.handleResetPassword}
        onRemoveRole={page.handleRemoveRole}
        onFinanceManager={page.handleFinanceManager}
        onSalesRoleUpdate={page.handleSalesRoleUpdate}
        onImpersonate={page.handleImpersonate}
      />
    </div>
  )
}
